import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Input, 
  InputNumber,
  Button, 
  Card, 
  Tag, 
  Typography, 
  Space, 
  Drawer,
  Spin,
  Pagination,
  Skeleton,
  Empty
} from 'antd';
import { 
  EnvironmentOutlined, 
  DollarOutlined,
  CalendarOutlined,
  EyeOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApiDefinition } from '../../config';
import apiService from '../../services/api';
import { filterQueryKeys, jobPostingQueryKeys } from '../../lib/queryClient';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import FilterSortBar from '../../components/Common/FilterSortBar';
import AdvertisementDisplay from '../../components/Common/AdvertisementDisplay';
import { 
  PublicJobPostingResponse, 
  PublicJobPostingSearchRequest,
  AdvertisementType
} from '../../types';
import dayjs from 'dayjs';
import styles from './JobPostings.module.css';

const { Title, Text } = Typography;

const JOB_POSTINGS_PAGE_SIZE = 5;

function parseJobPostingParamsFromUrl(sp: URLSearchParams): PublicJobPostingSearchRequest {
  return {
    page: sp.get('page') ? Number(sp.get('page')) : 1,
    pageSize: sp.get('pageSize') ? Number(sp.get('pageSize')) : JOB_POSTINGS_PAGE_SIZE,
    searchTerm: sp.get('search') || undefined,
    category: sp.get('category') || '',
    minExperience: sp.get('minExperience') ? Number(sp.get('minExperience')) : undefined,
    maxExperience: sp.get('maxExperience') ? Number(sp.get('maxExperience')) : undefined,
    minSalary: sp.get('minSalary') ? Number(sp.get('minSalary')) : undefined,
    maxSalary: sp.get('maxSalary') ? Number(sp.get('maxSalary')) : undefined,
    sortBy: sp.get('sortBy') || undefined,
    sortOrder: sp.get('sortOrder') || undefined
  };
}

type JobPostingDrawerFilters = Pick<PublicJobPostingSearchRequest, 'minExperience' | 'maxExperience' | 'minSalary' | 'maxSalary'>;

const JobPostings: React.FC = () => {
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState<JobPostingDrawerFilters>({
    minExperience: undefined,
    maxExperience: undefined,
    minSalary: undefined,
    maxSalary: undefined
  });
  const [searchParams, setSearchParams] = useState<PublicJobPostingSearchRequest>(() =>
    parseJobPostingParamsFromUrl(urlSearchParams)
  );

  useEffect(() => {
    const next = parseJobPostingParamsFromUrl(urlSearchParams);
    setSearchParams(prev =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [urlSearchParams]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchParams.searchTerm) params.search = searchParams.searchTerm;
    if (searchParams.category) params.category = searchParams.category;
    if (searchParams.minExperience != null) params.minExperience = String(searchParams.minExperience);
    if (searchParams.maxExperience != null) params.maxExperience = String(searchParams.maxExperience);
    if (searchParams.minSalary != null) params.minSalary = String(searchParams.minSalary);
    if (searchParams.maxSalary != null) params.maxSalary = String(searchParams.maxSalary);
    if (searchParams.sortBy) params.sortBy = searchParams.sortBy;
    if (searchParams.sortOrder) params.sortOrder = searchParams.sortOrder;
    if (searchParams.page !== 1) params.page = String(searchParams.page);
    if (searchParams.pageSize !== JOB_POSTINGS_PAGE_SIZE) params.pageSize = String(searchParams.pageSize);
    setUrlSearchParams(params, { replace: true });
  }, [searchParams]);

  const debouncedSearchParams = useDebouncedValue(searchParams, ApiDefinition.searchDebounceMs);

  const { data: filters } = useQuery({
    queryKey: filterQueryKeys.jobPosting,
    queryFn: async () => {
      const response = await apiService.getJobPostingFilters();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 30,
  });

  const { data: appliedJobIdsList = [] } = useQuery({
    queryKey: jobPostingQueryKeys.myApplicationIds,
    queryFn: async () => {
      const response = await apiService.getMyApplicationJobIds();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
  });

  const appliedJobIds = new Set(appliedJobIdsList);

  const { data: searchResult, isLoading: searchLoading } = useQuery({
    queryKey: jobPostingQueryKeys.search(debouncedSearchParams),
    queryFn: async () => {
      const response = await apiService.getPublicJobPostings(debouncedSearchParams);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const jobPostings = searchResult?.data ?? [];
  const pagination = {
    current: searchResult?.pageNumber ?? 1,
    pageSize: searchResult?.pageSize ?? JOB_POSTINGS_PAGE_SIZE,
    total: searchResult?.totalCount ?? 0
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setSearchParams(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleDrawerOpen = () => {
    setDrawerFilters({
      minExperience: searchParams.minExperience,
      maxExperience: searchParams.maxExperience,
      minSalary: searchParams.minSalary,
      maxSalary: searchParams.maxSalary
    });
    setFilterDrawerVisible(true);
  };

  const handleDrawerApply = () => {
    setSearchParams(prev => ({ ...prev, ...drawerFilters, page: 1 }));
    setFilterDrawerVisible(false);
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize ?? prev.pageSize
    }));
  };

  const handleResetFilters = () => {
    setSearchParams({ 
      page: 1, 
      pageSize: JOB_POSTINGS_PAGE_SIZE, 
      searchTerm: undefined,
      category: undefined,
      minExperience: undefined,
      maxExperience: undefined,
      minSalary: undefined,
      maxSalary: undefined,
      sortBy: undefined,
      sortOrder: undefined
    });
  };

  // Aktif filtreleri chip olarak göster
  const activeFilters = useMemo(() => {
    const filters: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (searchParams.category) {
      filters.push({
        key: 'category',
        label: `Kategori: ${searchParams.category}`,
        onRemove: () => handleFilterChange('category', undefined)
      });
    }
    if (searchParams.minExperience != null || searchParams.maxExperience != null) {
      const minStr = searchParams.minExperience != null ? `${searchParams.minExperience}` : '0';
      const maxStr = searchParams.maxExperience != null ? `${searchParams.maxExperience}` : '∞';
      filters.push({
        key: 'experience',
        label: `Deneyim: ${minStr} - ${maxStr} yıl`,
        onRemove: () => {
          handleFilterChange('minExperience', undefined);
          handleFilterChange('maxExperience', undefined);
        }
      });
    }
    if (searchParams.minSalary != null || searchParams.maxSalary != null) {
      filters.push({
        key: 'salary',
        label: `Maaş: ₺${searchParams.minSalary ?? 0} - ₺${searchParams.maxSalary ?? '∞'}`,
        onRemove: () => {
          handleFilterChange('minSalary', undefined);
          handleFilterChange('maxSalary', undefined);
        }
      });
    }
    return filters;
  }, [searchParams]);

  const hasActiveFilters = activeFilters.length > 0 || searchParams.sortBy != null;

  const formatSalary = (min?: number, max?: number) => {
    if (!min && !max) return 'Belirtilmemiş';
    if (min && max) return `₺${min.toLocaleString()} - ₺${max.toLocaleString()}`;
    if (min) return `₺${min.toLocaleString()}+`;
    if (max) return `₺${max.toLocaleString()}'ye kadar`;
    return 'Belirtilmemiş';
  };

  const getEmploymentTypeColor = (type?: string) => {
    switch (type?.toLowerCase()) {
      case 'full-time': return 'blue';
      case 'part-time': return 'green';
      case 'contract': return 'orange';
      case 'internship': return 'purple';
      default: return 'default';
    }
  };

  const translateEmploymentType = (type?: string): string => {
    if (!type) return 'Belirtilmemiş';
    switch (type.toLowerCase()) {
      case 'full-time': return 'Tam Zamanlı';
      case 'part-time': return 'Yarı Zamanlı';
      case 'contract': return 'Sözleşmeli';
      case 'internship': return 'Staj';
      default: return type;
    }
  };

  const getExperienceLevelColor = (level?: string) => {
    switch (level?.toLowerCase()) {
      case 'junior': return 'green';
      case 'mid': return 'blue';
      case 'senior': return 'red';
      case 'lead': return 'purple';
      default: return 'default';
    }
  };

  const handleSortChange = (value: string | undefined) => {
    if (!value) {
      setSearchParams(prev => ({ ...prev, sortBy: undefined, sortOrder: undefined, page: 1 }));
      return;
    }
    const [sortBy, sortOrder] = value.split('_');
    setSearchParams(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
  };

  const renderJobCardSkeleton = () => (
    <div className={styles.jobCardSkeleton}>
      <div className={styles.jobCardSkeletonBody}>
        <Skeleton active paragraph={false} title={{ width: '85%' }} />
        <Skeleton active paragraph={false} title={{ width: '50%' }} />
        <Skeleton active paragraph={false} title={{ width: '70%' }} />
        <Skeleton.Button active size="small" style={{ width: 100, height: 32 }} />
      </div>
    </div>
  );

  const renderJobCard = (job: PublicJobPostingResponse) => (
    <article
      key={job.id}
      className={styles.jobCard}
      onClick={() => navigate(`/job/${job.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/job/${job.id}`);
        }
      }}
    >
      <div className={styles.jobCardBody}>
        <div>
          <h2 className={styles.jobCardTitle}>{job.title}</h2>
          <div className={styles.jobCardCompany}>{job.companyName}</div>
          <div className={styles.jobCardMeta}>
            <span><EnvironmentOutlined /> {job.location || 'Belirtilmemiş'}</span>
            <span><DollarOutlined /> {formatSalary(job.salaryMin, job.salaryMax)}</span>
            {(job.minExperienceYears != null && job.maxExperienceYears != null) && (
              <span>{job.minExperienceYears}-{job.maxExperienceYears} yıl</span>
            )}
            {job.experienceLevel && job.minExperienceYears == null && job.maxExperienceYears == null && (
              <Tag color={getExperienceLevelColor(job.experienceLevel)}>{job.experienceLevel}</Tag>
            )}
            {job.employmentType && (
              <Tag color={getEmploymentTypeColor(job.employmentType)}>
                {translateEmploymentType(job.employmentType)}
              </Tag>
            )}
          </div>
        </div>
        <div className={styles.jobCardFooter}>
          <span className={styles.jobCardDeadline}>
            <CalendarOutlined /> Son başvuru: {dayjs(job.applicationDeadline).format('DD.MM.YYYY')}
          </span>
          <div className={styles.jobCardCta}>
            {appliedJobIds.has(job.id) && (
              <Tag color="success">Başvuruldu</Tag>
            )}
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/job/${job.id}`);
              }}
            >
              Detay
            </Button>
          </div>
        </div>
      </div>
    </article>
  );

  const renderAdvancedFilters = () => (
    <Drawer
      title="Gelişmiş Filtreler"
      placement="right"
      onClose={() => setFilterDrawerVisible(false)}
      open={filterDrawerVisible}
      width={400}
    >
      {filters && (
        <div>
          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Deneyim Aralığı (yıl)</Title>
            <Space.Compact style={{ width: '100%', gap: '8px' }}>
              <InputNumber
                placeholder="Min yıl"
                style={{ width: '100%' }}
                min={0}
                max={99}
                value={drawerFilters.minExperience ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, minExperience: v != null ? Number(v) : undefined }))}
                controls={false}
              />
              <InputNumber
                placeholder="Max yıl"
                style={{ width: '100%' }}
                min={0}
                max={99}
                value={drawerFilters.maxExperience ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, maxExperience: v != null ? Number(v) : undefined }))}
                controls={false}
              />
            </Space.Compact>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Maaş Aralığı (₺)</Title>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="Min"
                type="number"
                value={drawerFilters.minSalary ?? ''}
                onChange={(e) => setDrawerFilters(prev => ({ ...prev, minSalary: e.target.value ? Number(e.target.value) : undefined }))}
                style={{ flex: 1 }}
              />
              <Input
                placeholder="Max"
                type="number"
                value={drawerFilters.maxSalary ?? ''}
                onChange={(e) => setDrawerFilters(prev => ({ ...prev, maxSalary: e.target.value ? Number(e.target.value) : undefined }))}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <Button type="primary" block onClick={handleDrawerApply}>
            Filtreleri Uygula
          </Button>
        </div>
      )}
    </Drawer>
  );


  // Giriş yapmamış kullanıcılar için uyarı ekranı
  if (!isAuthenticated) {
    return (
      <div style={{ background: 'var(--bg-tertiary)', minHeight: '100vh', padding: '24px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '60px 40px' }}>
            <LockOutlined style={{ fontSize: '80px', color: 'var(--primary-500)', marginBottom: '24px' }} />
            <Title level={2} style={{ marginBottom: '16px' }}>
              İş İlanlarını Görmek İçin Giriş Yapmalısınız
            </Title>
            <Text type="secondary" style={{ fontSize: '16px', display: 'block', marginBottom: '32px' }}>
              İş ilanlarını görüntülemek ve başvuru yapmak için lütfen giriş yapın veya hesap oluşturun.
            </Text>
            <Space size="large">
              <Button 
                type="primary" 
                size="large"
                onClick={() => navigate('/login')}
                style={{ height: '48px', paddingLeft: '32px', paddingRight: '32px', fontSize: '16px' }}
              >
                Giriş Yap
              </Button>
              <Button 
                size="large"
                onClick={() => navigate('/register-mentee')}
                style={{ height: '48px', paddingLeft: '32px', paddingRight: '32px', fontSize: '16px' }}
              >
                Kayıt Ol
              </Button>
            </Space>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.jobsPage}>
      <div className={styles.container}>
        <div className={styles.hero}>
          <div className={styles.heroInner}>
            <h1 className={styles.heroTitle}>İş İlanları</h1>
            <p className={styles.heroSubtitle}>Hayalinizdeki işi bulun ve kariyerinizi şekillendirin</p>
          </div>
        </div>

        <div className={styles.adSlotBanner}>
          <AdvertisementDisplay pagePath="/job-postings" type={AdvertisementType.Banner} position="top" />
        </div>

        <FilterSortBar
          searchPlaceholder="İş ilanı, şirket veya pozisyon ara"
          searchValue={searchParams.searchTerm || ''}
          onSearchChange={(value) => handleFilterChange('searchTerm', value)}
          onSearch={handleSearch}
          categoryOptions={filters?.categories || []}
          categoryValue={searchParams.category}
          onCategoryChange={(value) => handleFilterChange('category', value)}
          sortOptions={[
            { value: '', label: 'Varsayılan' },
            { value: 'createdAt_desc', label: 'En yeni' },
            { value: 'createdAt_asc', label: 'En eski' },
            { value: 'title_asc', label: 'Başlık (A-Z)' },
            { value: 'title_desc', label: 'Başlık (Z-A)' },
            { value: 'applicationDeadline_asc', label: 'Son başvuru (yakından uzağa)' },
            { value: 'applicationDeadline_desc', label: 'Son başvuru (uzaktan yakına)' },
            { value: 'salaryMin_desc', label: 'Maaş (yüksek → düşük)' },
            { value: 'salaryMin_asc', label: 'Maaş (düşük → yüksek)' }
          ]}
          sortValue={searchParams.sortBy && searchParams.sortOrder ? `${searchParams.sortBy}_${searchParams.sortOrder}` : undefined}
          onSortChange={handleSortChange}
          showAdvancedFilters={true}
          onAdvancedFiltersOpen={handleDrawerOpen}
          advancedFilterCount={
            (searchParams.minExperience != null || searchParams.maxExperience != null ? 1 : 0) +
            (searchParams.minSalary != null || searchParams.maxSalary != null ? 1 : 0)
          }
          onReset={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          activeFilters={activeFilters}
          mobileBreakpoint="md"
        />

        <Spin spinning={searchLoading}>
          {searchLoading && jobPostings.length === 0 ? (
            <div className={styles.jobList}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`skeleton-${i}`}>{renderJobCardSkeleton()}</div>
              ))}
            </div>
          ) : jobPostings.length > 0 ? (
            <>
              <div className={styles.jobList}>
                {jobPostings.map((job) => renderJobCard(job))}
              </div>
              <div className={styles.adSlotCard}>
                <AdvertisementDisplay pagePath="/job-postings" type={AdvertisementType.Card} position="inline" />
              </div>
              <div className={styles.paginationWrap}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} iş ilanı`}
                />
              </div>
            </>
          ) : (
            <div className={styles.emptyWrap}>
              <Empty description="İş ilanı bulunamadı. Arama kriterlerinizi değiştirerek tekrar deneyin." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
        </Spin>

        {renderAdvancedFilters()}
      </div>
    </div>
  );
};

export default JobPostings;
